import React, { useState, useEffect } from 'react';
import { X, Play, Pause, SkipForward, SkipBack, Clock, Zap } from 'lucide-react';
import WeatherWidget from './widgets/WeatherWidget';
import ClockWidget from './widgets/ClockWidget';
import RSSWidget from './widgets/RSSWidget';
import StreamingWidget from './widgets/StreamingWidget';
import MultiZoneWidget from './widgets/MultiZoneWidget';

const API_BASE_URL = (process.env.REACT_APP_API_URL?.replace('/api', '') || (process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch'));

const PlaylistPreview = ({ playlist, media, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  // Get the current media item
  const currentItem = playlist.media_items?.[currentIndex];
  const totalItems = playlist.media_items?.length || 0;

  // Auto-advance to next item
  useEffect(() => {
    if (!isPlaying || !currentItem) return;

    const duration = (currentItem.duration || 10) * 1000; // Convert to milliseconds
    const interval = 50; // Update every 50ms for smooth progress bar
    const steps = duration / interval;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const newProgress = (currentStep / steps) * 100;
      
      if (newProgress >= 100) {
        // Move to next item
        handleNext();
      } else {
        setProgress(newProgress);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [isPlaying, currentIndex, currentItem]);

  const handleNext = () => {
    if (currentIndex < totalItems - 1) {
      setCurrentIndex(currentIndex + 1);
      setProgress(0);
    } else if (playlist.loop) {
      setCurrentIndex(0);
      setProgress(0);
    } else {
      setIsPlaying(false);
      setProgress(0);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setProgress(0);
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleItemClick = (index) => {
    setCurrentIndex(index);
    setProgress(0);
    setIsPlaying(true);
  };

  // Get media URL
  const getMediaUrl = (item) => {
    if (item.type === 'webpage') {
      return item.url;
    }
    return item.url?.startsWith('http') 
      ? item.url 
      : `${API_BASE_URL}${item.url}`;
  };

  // Check if item is a widget
  const isWidget = (item) => {
    return item.item_type === 'widget' || item.type === 'widget';
  };

  // ✅ Render the appropriate widget based on feature_id
  const renderWidget = (item) => {
    const config = item.widget_config || item.config;
    
    switch (item.feature_id) {
      case 'weather-widget':
        return <WeatherWidget config={config} isPreview={true} />;
      
      case 'clock-widget':
        return <ClockWidget config={config} />;
      
      case 'rss-feed-widget':
        return <RSSWidget config={config} />;

      case 'streaming-widget':
        return <StreamingWidget config={config} />;

      case 'multi-zone':
        // ✅ MULTIZONE FIX: organizationId hinzufügen für korrektes Laden der Inhalte
        return (
          <MultiZoneWidget 
            config={config} 
            screenSize={{ 
              width: window.innerWidth, 
              height: window.innerHeight, 
              fontSizeMultiplier: 1, 
              spacingMultiplier: 1 
            }}
            organizationId={playlist?.organization_id}
          />
        );
      
      // Add more widgets here as they are implemented
      
      default:
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center text-white">
              <Zap size={48} className="mx-auto mb-4 text-purple-400" />
              <p className="text-lg">Unknown Widget Type</p>
              <p className="text-sm text-gray-400 mt-2">{item.feature_id}</p>
            </div>
          </div>
        );
    }
  };

  if (!playlist || totalItems === 0) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            No Media in Playlist
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This playlist doesn't have any media items yet.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/95 flex flex-col z-50">
      {/* Header */}
      <div className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{playlist.name}</h2>
            <p className="text-sm text-gray-400">
              {currentIndex + 1} / {totalItems} items
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            title="Close Preview"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div className="w-full h-full max-w-6xl max-h-[70vh] bg-black rounded-lg overflow-hidden shadow-2xl">
          {currentItem && (
            <>
              {/* Widgets - Dynamic Rendering */}
              {isWidget(currentItem) && (
                <div className="w-full h-full flex items-center justify-center">
                  {renderWidget(currentItem)}
                </div>
              )}

              {/* Image */}
              {!isWidget(currentItem) && currentItem.type === 'image' && (
                <img
                  src={getMediaUrl(currentItem)}
                  alt={currentItem.name}
                  className="w-full h-full object-contain"
                />
              )}

              {/* Video */}
              {!isWidget(currentItem) && currentItem.type === 'video' && (
                <video
                  src={getMediaUrl(currentItem)}
                  className="w-full h-full object-contain"
                  autoPlay={isPlaying}
                  controls
                />
              )}

              {/* Webpage */}
              {!isWidget(currentItem) && currentItem.type === 'webpage' && (
                <iframe
                  src={currentItem.url}
                  className="w-full h-full"
                  title={currentItem.name}
                />
              )}

              {/* PDF */}
              {!isWidget(currentItem) && currentItem.type === 'pdf' && (
                <iframe
                  src={getMediaUrl(currentItem)}
                  className="w-full h-full"
                  title={currentItem.name}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="bg-gray-900/50 backdrop-blur-sm border-t border-gray-700 p-4">
        <div className="max-w-7xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
              <span className="flex items-center gap-2">
                {currentItem?.name || 'No media'}
                {isWidget(currentItem) && (
                  <span className="bg-purple-600 text-white px-2 py-0.5 rounded text-xs">
                    Widget
                  </span>
                )}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {currentItem?.duration || 0}s
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-primary-600 h-full transition-all duration-100"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Playback Controls */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="text-white hover:text-primary-400 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
              title="Previous"
            >
              <SkipBack size={24} />
            </button>

            <button
              onClick={handlePlayPause}
              className="bg-primary-600 hover:bg-primary-700 text-white p-4 rounded-full transition-colors"
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>

            <button
              onClick={handleNext}
              disabled={currentIndex === totalItems - 1 && !playlist.loop}
              className="text-white hover:text-primary-400 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
              title="Next"
            >
              <SkipForward size={24} />
            </button>
          </div>

          {/* Thumbnail Timeline */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
            {playlist.media_items.map((item, index) => (
              <button
                key={index}
                onClick={() => handleItemClick(index)}
                className={`flex-shrink-0 relative group ${
                  index === currentIndex
                    ? 'ring-2 ring-blue-500'
                    : 'ring-1 ring-gray-600 hover:ring-gray-400'
                } rounded overflow-hidden transition-all`}
                title={item.name}
              >
                <div className="w-24 h-16 bg-gray-800 flex items-center justify-center">
                  {isWidget(item) && (
                    <div className="text-purple-400">
                      <Zap size={20} />
                    </div>
                  )}
                  {!isWidget(item) && item.type === 'image' && (
                    <img
  src={item.thumbnail_url ? `${API_BASE_URL}${item.thumbnail_url}` : getMediaUrl(item)}
  alt={item.name}
  className="w-full h-full object-cover"
  loading="lazy"
/>
                  )}
                  {!isWidget(item) && item.type === 'video' && (
                    <div className="text-purple-400">
                      <Play size={20} />
                    </div>
                  )}
                  {!isWidget(item) && item.type === 'webpage' && (
                    <div className="text-green-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 truncate">
                  {index + 1}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaylistPreview;